import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Debounces a value for search inputs. `flush` applies immediately (e.g. on Enter).
 * @param {string} value
 * @param {number} [delayMs=400]
 * @returns {{ debounced: string, flush: () => void }}
 */
export function useDebouncedValue(value, delayMs = 400) {
  const [debounced, setDebounced] = useState(value);
  const timeoutRef = useRef(null);

  const flush = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setDebounced(value);
  }, [value]);

  useEffect(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      setDebounced(value);
    }, delayMs);

    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [value, delayMs]);

  return { debounced, flush };
}
